import React from 'react';
import { useState, useEffect, useRef } from "react";

// ─── BRAND SYSTEM ───
const BRAND = {
  black: "#0a0a0a",
  darkGray: "#141414",
  midGray: "#1e1e1e",
  borderGray: "#2a2a2a",
  textMuted: "#666666",
  textSecondary: "#999999",
  textPrimary: "#e0e0e0",
  white: "#ffffff",
  accent: "#00ff88",
  accentDim: "#00cc6a",
  danger: "#ff4444",
  warning: "#ffaa00",
};

const SHIPPING_FEE = 8;

// ─── SQUARE CONFIG ───
const SQUARE_CONFIG = {
  applicationId: "sq0idp-vuJdsQtWfRptxUO4RSCnEg",
  locationId: "L6K1Y9T2MNXCC",
  sdkUrl: "https://web.squarecdn.com/v1/square.js",
};

const API_URL = "/api/process-payment";

// ─── FLASK + BOLT LOGO ───
const LogoIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <path d="M38 10 L38 38 L18 75 Q15 82 20 88 Q25 94 33 94 L67 94 Q75 94 80 88 Q85 82 82 75 L62 38 L62 10"
      stroke={BRAND.white} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="34" y1="10" x2="66" y2="10" stroke={BRAND.white} strokeWidth="3.5" strokeLinecap="round" />
    <line x1="30" y1="65" x2="70" y2="65" stroke={BRAND.white} strokeWidth="2" opacity="0.3" />
    <path d="M55 30 L45 50 L53 50 L43 72"
      stroke={BRAND.accent} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── PRODUCTS ───
const PRODUCTS = [
  {
    id: "LAB-RK-001",
    name: "Recovery Protocol Kit",
    subtitle: "Standard",
    description: "Hydration, fuel, and reset essentials. Our core recovery system designed to help your body recover efficiently.",
    contents: ["Coconut water", "Electrolyte packet", "Protein bar", "Ginger chews", "Protocol card"],
    price: 18,
    badge: null,
  },
  {
    id: "LAB-RK-PLUS-001",
    name: "Recovery Protocol+",
    subtitle: "Enhanced",
    description: "Everything in the standard kit plus extra hydration and enhanced reset support. Most people choose this.",
    contents: ["Coconut water", "2x Electrolyte packets", "Protein bar", "Ginger chews", "Extra hydration", "Protocol card"],
    price: 21,
    badge: "MOST POPULAR",
  },
];

// ─── VAN SCHEDULE ───
const DEFAULT_SCHEDULE = [
  { day: "Friday", time: "10:30 PM - 1:00 AM", zone: "Night Exit Zone", location: "4th Ave District", status: "active" },
  { day: "Saturday", time: "9:00 AM - 11:30 AM", zone: "Morning-After Zone", location: "Downtown Brunch District", status: "active" },
  { day: "Saturday", time: "12:00 PM - 2:00 PM", zone: "Gym / Trail Zone", location: "Sabino Canyon Area", status: "upcoming" },
  { day: "Sunday", time: "9:00 AM - 12:00 PM", zone: "Morning-After Zone", location: "Congress St / Hotel Row", status: "upcoming" },
];

// ─── UTILITY COMPONENTS ───
const Badge = ({ children, color = BRAND.accent }) => (
  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: BRAND.black, background: color, padding: "3px 8px", borderRadius: 3 }}>{children}</span>
);

const StatusDot = ({ status }) => {
  const colors = { active: BRAND.accent, upcoming: BRAND.textMuted, offline: BRAND.danger };
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colors[status] || BRAND.textMuted, boxShadow: status === "active" ? `0 0 8px ${BRAND.accent}` : "none" }} />;
};

const Button = ({ children, onClick, variant = "primary", style: s = {}, disabled = false }) => {
  const [hover, setHover] = useState(false);
  const styles = {
    primary: { background: hover && !disabled ? BRAND.white : BRAND.accent, color: BRAND.black, border: "none" },
    secondary: { background: hover && !disabled ? BRAND.midGray : "transparent", color: BRAND.white, border: `1px solid ${BRAND.borderGray}` },
    danger: { background: hover && !disabled ? "#ff6666" : BRAND.danger, color: BRAND.white, border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ padding: "12px 24px", borderRadius: 6, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1, transition: "all 0.2s ease", fontFamily: "'DM Sans', sans-serif", ...styles[variant], ...s }}>
      {children}
    </button>
  );
};

const Spinner = () => (
  <div style={{ width: 20, height: 20, border: `2px solid ${BRAND.borderGray}`, borderTopColor: BRAND.accent, borderRadius: "50%", animation: "spin 0.6s linear infinite", display: "inline-block" }} />
);

// ─── CART HOOK ───
const useCart = () => {
  const [items, setItems] = useState([]);
  const addItem = (product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { ...product, qty: 1 }];
    });
  };
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) => {
    if (qty < 1) return removeItem(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };
  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const count = items.reduce((sum, i) => sum + i.qty, 0);
  const total = count > 0 ? subtotal + SHIPPING_FEE : 0;
  return { items, addItem, removeItem, updateQty, subtotal, total, count, clear: () => setItems([]) };
};

// ─── SQUARE PAYMENT FORM WITH SHIPPING ───
const SquarePaymentForm = ({ subtotal, shippingFee, totalAmount, cartItems, onSuccess, onError, onCancel }) => {
  const cardContainerRef = useRef(null);
  const cardRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [shippingInfo, setShippingInfo] = useState({ street: "", apt: "", city: "", state: "AZ", zip: "" });
  const [step, setStep] = useState("shipping"); // "shipping" | "payment"

  useEffect(() => {
    if (window.Square) { setSdkReady(true); return; }
    const existingScript = document.querySelector('script[src*="squarecdn.com"]');
    if (existingScript) { existingScript.addEventListener("load", () => setSdkReady(true)); return; }
    const script = document.createElement("script");
    script.src = SQUARE_CONFIG.sdkUrl;
    script.onload = () => setSdkReady(true);
    script.onerror = () => { setError("Could not load payment system."); setLoading(false); };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!sdkReady || !cardContainerRef.current || step !== "payment") return;
    let cancelled = false;
    const init = async () => {
      try {
        if (!window.Square) throw new Error("Square SDK not available");
        const payments = window.Square.payments(SQUARE_CONFIG.applicationId, SQUARE_CONFIG.locationId);
        const card = await payments.card();
        if (cancelled) return;
        await card.attach(cardContainerRef.current);
        cardRef.current = card;
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError("Payment system initialization failed."); setLoading(false); }
      }
    };
    setLoading(true);
    init();
    return () => { cancelled = true; };
  }, [sdkReady, step]);

  const validateShipping = () => {
    if (!customerInfo.name.trim()) { setError("Please enter your name."); return false; }
    if (!shippingInfo.street.trim()) { setError("Please enter your street address."); return false; }
    if (!shippingInfo.city.trim()) { setError("Please enter your city."); return false; }
    if (!shippingInfo.zip.trim() || shippingInfo.zip.length < 5) { setError("Please enter a valid ZIP code."); return false; }
    if (shippingInfo.state !== "AZ") { setError("We currently only ship within Arizona. Nationwide shipping coming soon!"); return false; }
    const azZipPrefixes = ["850", "851", "852", "853", "854", "855", "856", "857", "858", "859", "860", "861", "863", "864", "865"];
    const zipPrefix = shippingInfo.zip.substring(0, 3);
    if (!azZipPrefixes.includes(zipPrefix)) { setError("That ZIP code doesn't appear to be in Arizona. We currently only ship within AZ."); return false; }
    setError(null);
    return true;
  };

  const handleContinueToPayment = () => {
    if (validateShipping()) setStep("payment");
  };

  const handlePayment = async () => {
    if (!cardRef.current) return;
    setProcessing(true);
    setError(null);
    try {
      const tokenResult = await cardRef.current.tokenize();
      if (tokenResult.status !== "OK") { setError("Payment could not be processed. Please check your card details."); setProcessing(false); return; }
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceId: tokenResult.token,
          amount: totalAmount,
          customer: customerInfo,
          shipping: shippingInfo,
          items: cartItems.map(item => ({ name: item.name, qty: item.qty, price: item.price })),
        }),
      });
      const data = await response.json();
      if (data.success) {
        onSuccess({ paymentId: data.paymentId, customer: customerInfo, shipping: shippingInfo, amount: totalAmount });
      } else {
        setError(data.error || "Payment failed. Please try again.");
      }
    } catch (e) {
      setError("Payment failed. Please check your connection and try again.");
    }
    setProcessing(false);
  };

  const inputStyle = { width: "100%", padding: "12px 14px", background: BRAND.black, color: BRAND.white, border: `1px solid ${BRAND.borderGray}`, borderRadius: 6, fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textMuted, marginBottom: 6 };

  // ─── SHIPPING STEP ───
  if (step === "shipping") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 16 }}>Contact Info</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input type="text" placeholder="Your name" value={customerInfo.name} onChange={(e) => setCustomerInfo(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" placeholder="email@example.com" value={customerInfo.email} onChange={(e) => setCustomerInfo(p => ({ ...p, email: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input type="tel" placeholder="(555) 555-5555" value={customerInfo.phone} onChange={(e) => setCustomerInfo(p => ({ ...p, phone: e.target.value }))} style={inputStyle} />
            </div>
          </div>
        </div>

        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 16 }}>Shipping Address</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={labelStyle}>Street Address *</label>
              <input type="text" placeholder="123 Main St" value={shippingInfo.street} onChange={(e) => setShippingInfo(p => ({ ...p, street: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Apt / Suite / Unit</label>
              <input type="text" placeholder="Apt 4B (optional)" value={shippingInfo.apt} onChange={(e) => setShippingInfo(p => ({ ...p, apt: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>City *</label>
                <input type="text" placeholder="Tucson" value={shippingInfo.city} onChange={(e) => setShippingInfo(p => ({ ...p, city: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>State</label>
                <div style={{ ...inputStyle, display: "flex", alignItems: "center", color: BRAND.accent, fontWeight: 700 }}>AZ</div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>ZIP *</label>
                <input type="text" placeholder="85701" maxLength={5} value={shippingInfo.zip} onChange={(e) => setShippingInfo(p => ({ ...p, zip: e.target.value.replace(/\D/g, "") }))} style={inputStyle} />
              </div>
            </div>
          </div>
          <p style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 8 }}>Currently shipping within Arizona only. Nationwide coming soon.</p>
        </div>

        {error && (
          <div style={{ padding: "12px 16px", background: BRAND.danger + "15", border: `1px solid ${BRAND.danger}33`, borderRadius: 6, fontSize: 13, color: BRAND.danger }}>{error}</div>
        )}

        <div style={{ padding: 18, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Subtotal</span>
            <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${subtotal}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Shipping (AZ)</span>
            <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${shippingFee}</span>
          </div>
          <div style={{ height: 1, background: BRAND.borderGray, margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: BRAND.white }}>Total</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${totalAmount}</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <Button variant="secondary" onClick={onCancel} style={{ flex: 1 }}>Back</Button>
          <Button onClick={handleContinueToPayment} style={{ flex: 2 }}>Continue to Payment</Button>
        </div>

        <p style={{ fontSize: 10, color: BRAND.textMuted, textAlign: "center" }}>Wellness support only. Not medical treatment.</p>
      </div>
    );
  }

  // ─── PAYMENT STEP ───
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ padding: 16, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}` }}>
        <p style={{ margin: 0, fontSize: 12, color: BRAND.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>Shipping to</p>
        <p style={{ margin: 0, fontSize: 14, color: BRAND.white }}>{customerInfo.name}</p>
        <p style={{ margin: "2px 0 0", fontSize: 13, color: BRAND.textSecondary }}>
          {shippingInfo.street}{shippingInfo.apt ? `, ${shippingInfo.apt}` : ""}, {shippingInfo.city}, AZ {shippingInfo.zip}
        </p>
        <button onClick={() => { setStep("shipping"); setError(null); }} style={{ background: "none", border: "none", color: BRAND.accent, fontSize: 12, cursor: "pointer", padding: 0, marginTop: 8 }}>Edit address</button>
      </div>

      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 16 }}>Payment</h3>
        <div style={{ background: BRAND.black, border: `1px solid ${BRAND.borderGray}`, borderRadius: 8, padding: 16, minHeight: 50 }}>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 12 }}>
              <Spinner />
              <span style={{ fontSize: 13, color: BRAND.textMuted }}>Loading payment form...</span>
            </div>
          )}
          <div ref={cardContainerRef} style={{ display: loading ? "none" : "block" }} />
        </div>
      </div>

      {error && (
        <div style={{ padding: "12px 16px", background: BRAND.danger + "15", border: `1px solid ${BRAND.danger}33`, borderRadius: 6, fontSize: 13, color: BRAND.danger }}>{error}</div>
      )}

      <div style={{ padding: 18, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Subtotal</span>
          <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${subtotal}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Shipping</span>
          <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${shippingFee}</span>
        </div>
        <div style={{ height: 1, background: BRAND.borderGray, margin: "8px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: BRAND.white }}>Total</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${totalAmount}</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <Button variant="secondary" onClick={() => setStep("shipping")} style={{ flex: 1 }}>Back</Button>
        <Button onClick={handlePayment} disabled={processing || loading} style={{ flex: 2 }}>
          {processing ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spinner /> Processing...</span> : `Pay $${totalAmount}`}
        </Button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: 0.4 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={BRAND.textMuted} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
        <span style={{ fontSize: 10, color: BRAND.textMuted, letterSpacing: "0.06em" }}>Secured by Square</span>
      </div>
      <p style={{ fontSize: 10, color: BRAND.textMuted, textAlign: "center" }}>Wellness support only. Not medical treatment.</p>
    </div>
  );
};

// ─── ORDER CONFIRMATION ───
const OrderConfirmation = ({ orderData, onDone }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 24, padding: "48px 24px", paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)", textAlign: "center" }}>
    <div style={{ width: 64, height: 64, borderRadius: "50%", background: BRAND.accent + "20", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${BRAND.accent}` }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={BRAND.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    </div>
    <div>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>Protocol Confirmed</h2>
      <p style={{ margin: "8px 0 0", fontSize: 14, color: BRAND.textSecondary }}>Your recovery kit is being prepared for shipment.</p>
    </div>
    <div style={{ padding: 20, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, width: "100%", maxWidth: 320, textAlign: "left" }}>
      <p style={{ margin: 0, fontSize: 13, color: BRAND.textSecondary }}>
        Order total: <span style={{ color: BRAND.accent, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>${orderData.amount}</span>
      </p>
      {orderData.customer?.name && <p style={{ margin: "8px 0 0", fontSize: 13, color: BRAND.textSecondary }}>Name: <span style={{ color: BRAND.white }}>{orderData.customer.name}</span></p>}
      {orderData.shipping && (
        <p style={{ margin: "8px 0 0", fontSize: 13, color: BRAND.textSecondary }}>
          Shipping to: <span style={{ color: BRAND.white }}>{orderData.shipping.street}{orderData.shipping.apt ? `, ${orderData.shipping.apt}` : ""}, {orderData.shipping.city}, AZ {orderData.shipping.zip}</span>
        </p>
      )}
      {orderData.paymentId && <p style={{ margin: "8px 0 0", fontSize: 11, color: BRAND.textMuted }}>Confirmation: {orderData.paymentId.slice(0, 12)}...</p>}
    </div>
    <p style={{ fontSize: 12, color: BRAND.textMuted, maxWidth: 300, lineHeight: 1.6 }}>
      Your kit will ship via USPS within 1-2 business days. Follow @thelabrecovery for updates.
    </p>
    <Button onClick={onDone}>Done</Button>
  </div>
);

// ─── PRODUCT CARD ───
const ProductCard = ({ product, onAdd }) => {
  const [hover, setHover] = useState(false);
  const [added, setAdded] = useState(false);
  const handleAdd = () => { onAdd(product); setAdded(true); setTimeout(() => setAdded(false), 1200); };
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: hover ? BRAND.midGray : BRAND.darkGray, border: `1px solid ${hover ? BRAND.accent + "44" : BRAND.borderGray}`, borderRadius: 10, padding: 24, transition: "all 0.25s ease", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: BRAND.textMuted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{product.subtitle}</span>
            {product.badge && <Badge>{product.badge}</Badge>}
          </div>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>{product.name}</h3>
        </div>
        <span style={{ fontSize: 28, fontWeight: 800, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${product.price}</span>
      </div>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: BRAND.textSecondary }}>{product.description}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {product.contents.map((item, i) => (
          <span key={i} style={{ fontSize: 11, color: BRAND.textMuted, background: BRAND.black, padding: "4px 10px", borderRadius: 4, border: `1px solid ${BRAND.borderGray}` }}>{item}</span>
        ))}
      </div>
      <Button onClick={handleAdd} style={{ width: "100%", marginTop: 4 }}>{added ? "Added" : "Add to Order"}</Button>
    </div>
  );
};

// ─── CART DRAWER ───
const CartDrawer = ({ cart, open, onClose, onCheckout }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 400, background: BRAND.darkGray, borderLeft: `1px solid ${BRAND.borderGray}`, display: "flex", flexDirection: "column", animation: "slideIn 0.25s ease" }}>
        <div style={{ padding: "20px 24px", paddingTop: "calc(env(safe-area-inset-top, 0px) + 20px)", borderBottom: `1px solid ${BRAND.borderGray}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>Your Order</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: BRAND.textMuted, fontSize: 24, cursor: "pointer", padding: 4 }}>x</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {cart.items.length === 0 ? (
            <p style={{ color: BRAND.textMuted, textAlign: "center", marginTop: 40 }}>No items yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {cart.items.map((item) => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 16, background: BRAND.midGray, borderRadius: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: BRAND.white }}>{item.name}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${item.price} x {item.qty}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => cart.updateQty(item.id, item.qty - 1)} style={{ width: 28, height: 28, borderRadius: 4, border: `1px solid ${BRAND.borderGray}`, background: "transparent", color: BRAND.white, cursor: "pointer", fontSize: 16 }}>-</button>
                    <span style={{ color: BRAND.white, fontSize: 14, fontWeight: 600, minWidth: 20, textAlign: "center" }}>{item.qty}</span>
                    <button onClick={() => cart.updateQty(item.id, item.qty + 1)} style={{ width: 28, height: 28, borderRadius: 4, border: `1px solid ${BRAND.borderGray}`, background: "transparent", color: BRAND.white, cursor: "pointer", fontSize: 16 }}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {cart.items.length > 0 && (
          <div style={{ padding: 24, borderTop: `1px solid ${BRAND.borderGray}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Subtotal</span>
              <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${cart.subtotal}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: BRAND.textSecondary }}>Shipping</span>
              <span style={{ fontSize: 13, color: BRAND.white, fontFamily: "'DM Mono', monospace" }}>${SHIPPING_FEE}</span>
            </div>
            <div style={{ height: 1, background: BRAND.borderGray, margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: BRAND.white }}>Total</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${cart.total}</span>
            </div>
            <Button onClick={onCheckout} style={{ width: "100%" }}>Checkout - ${cart.total}</Button>
            <p style={{ margin: "12px 0 0", fontSize: 11, color: BRAND.textMuted, textAlign: "center" }}>Flat rate shipping within Arizona.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── VAN SCHEDULE ───
const VanSchedule = ({ schedule, isOps = false, onToggle }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {schedule.map((s, i) => (
      <div key={i} style={{ padding: 18, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <StatusDot status={s.status} />
            <span style={{ fontSize: 14, fontWeight: 700, color: BRAND.white }}>{s.day}</span>
            <span style={{ fontSize: 12, color: BRAND.textMuted }}>-</span>
            <span style={{ fontSize: 12, color: BRAND.textSecondary }}>{s.time}</span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: BRAND.textSecondary }}>{s.location} - <span style={{ color: BRAND.textMuted, fontStyle: "italic" }}>{s.zone}</span></p>
        </div>
        {isOps && onToggle && (
          <Button variant={s.status === "active" ? "danger" : "secondary"} onClick={() => onToggle(i)} style={{ padding: "8px 16px", fontSize: 11 }}>
            {s.status === "active" ? "Set Offline" : "Set Active"}
          </Button>
        )}
      </div>
    ))}
  </div>
);

// ─── OPS DASHBOARD ───
const OpsDashboard = ({ schedule, setSchedule, onLogout }) => {
  const [salesLog, setSalesLog] = useState([
    { id: 1, time: "9:42 AM", product: "Recovery Protocol Kit", qty: 1, total: 18 },
    { id: 2, time: "10:15 AM", product: "Recovery Protocol+", qty: 2, total: 42 },
    { id: 3, time: "10:48 AM", product: "Recovery Protocol Kit", qty: 1, total: 18 },
  ]);
  const [inventory, setInventory] = useState({ standard: 78, plus: 22 });
  const [newSaleProduct, setNewSaleProduct] = useState("standard");
  const [newSaleQty, setNewSaleQty] = useState(1);
  const toggleSchedule = (index) => { setSchedule((prev) => prev.map((s, i) => i === index ? { ...s, status: s.status === "active" ? "offline" : "active" } : s)); };
  const logSale = () => {
    const product = newSaleProduct === "standard" ? "Recovery Protocol Kit" : "Recovery Protocol+";
    const price = newSaleProduct === "standard" ? 18 : 21;
    const total = price * newSaleQty;
    const time = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    setSalesLog((prev) => [{ id: Date.now(), time, product, qty: newSaleQty, total }, ...prev]);
    setInventory((prev) => ({ ...prev, [newSaleProduct]: Math.max(0, prev[newSaleProduct] - newSaleQty) }));
    setNewSaleQty(1);
  };
  const totalRevenue = salesLog.reduce((sum, s) => sum + s.total, 0);
  const totalUnits = salesLog.reduce((sum, s) => sum + s.qty, 0);
  const selectStyle = { padding: "10px 14px", background: BRAND.black, color: BRAND.white, border: `1px solid ${BRAND.borderGray}`, borderRadius: 6, fontSize: 13, fontFamily: "'DM Sans', sans-serif", flex: 1, minWidth: 160 };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px", paddingTop: "calc(env(safe-area-inset-top, 0px) + 24px)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LogoIcon size={32} />
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>OPS DASHBOARD</h1>
            <span style={{ fontSize: 10, letterSpacing: "0.12em", color: BRAND.accent, textTransform: "uppercase" }}>Operator Console</span>
          </div>
        </div>
        <Button variant="secondary" onClick={onLogout} style={{ padding: "8px 16px", fontSize: 11 }}>Exit Ops</Button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 32 }}>
        {[
          { label: "Revenue Today", value: `$${totalRevenue}`, color: BRAND.accent },
          { label: "Units Sold", value: totalUnits, color: BRAND.white },
          { label: "Standard Stock", value: inventory.standard, color: inventory.standard < 20 ? BRAND.danger : BRAND.white },
          { label: "Protocol+ Stock", value: inventory.plus, color: inventory.plus < 10 ? BRAND.danger : BRAND.white },
        ].map((stat, i) => (
          <div key={i} style={{ padding: 18, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, textAlign: "center" }}>
            <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: BRAND.textMuted }}>{stat.label}</p>
            <p style={{ margin: "8px 0 0", fontSize: 28, fontWeight: 800, color: stat.color, fontFamily: "'DM Mono', monospace" }}>{stat.value}</p>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 12 }}>Log a Sale</h2>
        <div style={{ display: "flex", gap: 12, padding: 18, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, flexWrap: "wrap", alignItems: "center" }}>
          <select value={newSaleProduct} onChange={(e) => setNewSaleProduct(e.target.value)} style={selectStyle}>
            <option value="standard">Standard Kit - $18</option>
            <option value="plus">Protocol+ - $21</option>
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: BRAND.textMuted }}>QTY</span>
            <input type="number" min="1" max="20" value={newSaleQty} onChange={(e) => setNewSaleQty(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: 56, padding: "10px 8px", background: BRAND.black, color: BRAND.white, border: `1px solid ${BRAND.borderGray}`, borderRadius: 6, fontSize: 14, textAlign: "center", fontFamily: "'DM Mono', monospace" }} />
          </div>
          <Button onClick={logSale}>Log Sale</Button>
        </div>
      </div>
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 12 }}>Sales Log</h2>
        <div style={{ background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, overflow: "hidden" }}>
          {salesLog.length === 0 ? <p style={{ padding: 24, color: BRAND.textMuted, textAlign: "center" }}>No sales logged yet.</p> :
            salesLog.map((sale, i) => (
              <div key={sale.id} style={{ padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: i < salesLog.length - 1 ? `1px solid ${BRAND.borderGray}` : "none" }}>
                <div><span style={{ fontSize: 13, fontWeight: 600, color: BRAND.white }}>{sale.product}</span><span style={{ fontSize: 12, color: BRAND.textMuted, marginLeft: 8 }}>x{sale.qty}</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}><span style={{ fontSize: 12, color: BRAND.textMuted }}>{sale.time}</span><span style={{ fontSize: 14, fontWeight: 700, color: BRAND.accent, fontFamily: "'DM Mono', monospace" }}>${sale.total}</span></div>
              </div>
            ))}
        </div>
      </div>
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.textSecondary, marginBottom: 12 }}>Van Schedule</h2>
        <VanSchedule schedule={schedule} isOps onToggle={toggleSchedule} />
      </div>
    </div>
  );
};

// ─── OPS LOGIN ───
const OpsLogin = ({ onLogin }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const handleSubmit = () => { if (pin === "2025") { onLogin(); } else { setError(true); setTimeout(() => setError(false), 2000); } };
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 24, paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <LogoIcon size={56} />
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: BRAND.white }}>Operator Access</h2>
      <p style={{ margin: 0, fontSize: 12, color: BRAND.textMuted }}>Enter your 4-digit PIN</p>
      <input type="password" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && handleSubmit()} placeholder="----"
        style={{ width: 160, padding: "14px 0", background: BRAND.midGray, color: BRAND.white, border: `1px solid ${error ? BRAND.danger : BRAND.borderGray}`, borderRadius: 8, fontSize: 24, textAlign: "center", letterSpacing: "0.3em", fontFamily: "'DM Mono', monospace", outline: "none" }} />
      {error && <p style={{ margin: 0, fontSize: 12, color: BRAND.danger }}>Invalid PIN</p>}
      <Button onClick={handleSubmit} disabled={pin.length < 4}>Access Dashboard</Button>
      <p style={{ fontSize: 10, color: BRAND.textMuted }}>Default PIN: 2025</p>
    </div>
  );
};

// ─── MAIN APP ───
export default function TheLabApp() {
  const [view, setView] = useState("customer");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutView, setCheckoutView] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [activeTab, setActiveTab] = useState("kits");
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const cart = useCart();

  useEffect(() => {
    const href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap";
    if (!document.querySelector(`link[href="${href}"]`)) { const link = document.createElement("link"); link.href = href; link.rel = "stylesheet"; document.head.appendChild(link); }
  }, []);

  const handleCheckout = () => { setCartOpen(false); setCheckoutView("payment"); };
  const handlePaymentSuccess = (data) => { setOrderData(data); setCheckoutView("confirmation"); cart.clear(); };
  const handleOrderDone = () => { setCheckoutView(null); setOrderData(null); };

  if (view === "ops-login") {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.black, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>
        <OpsLogin onLogin={() => setView("ops")} />
        <div style={{ textAlign: "center", paddingBottom: 24 }}>
          <button onClick={() => setView("customer")} style={{ background: "none", border: "none", color: BRAND.textMuted, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>Back to storefront</button>
        </div>
      </div>
    );
  }
  if (view === "ops") {
    return <div style={{ minHeight: "100vh", background: BRAND.black, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}><OpsDashboard schedule={schedule} setSchedule={setSchedule} onLogout={() => setView("customer")} /></div>;
  }
  if (checkoutView === "payment") {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.black, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "32px 20px", paddingTop: "calc(env(safe-area-inset-top, 0px) + 32px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
            <LogoIcon size={32} />
            <div>
              <h1 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: "0.08em", color: BRAND.white }}>CHECKOUT</h1>
              <span style={{ fontSize: 9, letterSpacing: "0.14em", color: BRAND.accent, textTransform: "uppercase" }}>Secure Payment</span>
            </div>
          </div>
          <SquarePaymentForm subtotal={cart.subtotal} shippingFee={SHIPPING_FEE} totalAmount={cart.total} cartItems={cart.items} onSuccess={handlePaymentSuccess} onError={(err) => console.error(err)} onCancel={() => setCheckoutView(null)} />
        </div>
      </div>
    );
  }
  if (checkoutView === "confirmation") {
    return <div style={{ minHeight: "100vh", background: BRAND.black, color: BRAND.white, fontFamily: "'DM Sans', sans-serif" }}><OrderConfirmation orderData={orderData} onDone={handleOrderDone} /></div>;
  }

  const tabs = [{ id: "kits", label: "Recovery Kits" }, { id: "schedule", label: "Find the Van" }, { id: "protocol", label: "The Protocol" }];

  return (
    <div style={{ minHeight: "100vh", background: BRAND.black, color: BRAND.white, fontFamily: "'DM Sans', sans-serif", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${BRAND.black}; }
        ::-webkit-scrollbar-thumb { background: ${BRAND.borderGray}; border-radius: 4px; }
      `}</style>

      <header style={{ padding: "16px 20px", paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)", borderBottom: `1px solid ${BRAND.borderGray}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: BRAND.black + "ee", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LogoIcon size={34} />
          <div>
            <h1 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: "0.08em", color: BRAND.white }}>THE LAB</h1>
            <span style={{ fontSize: 9, letterSpacing: "0.14em", color: BRAND.textMuted, textTransform: "uppercase" }}>Recovery Protocol</span>
          </div>
        </div>
        <button onClick={() => setCartOpen(true)} style={{ position: "relative", background: "none", border: `1px solid ${BRAND.borderGray}`, borderRadius: 8, padding: "8px 14px", color: BRAND.white, cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>
          Order
          {cart.count > 0 && <span style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: BRAND.accent, color: BRAND.black, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{cart.count}</span>}
        </button>
      </header>

      <div style={{ padding: "48px 20px 32px", textAlign: "center", animation: "fadeUp 0.6s ease" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><LogoIcon size={64} /></div>
        <h2 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "0.04em", color: BRAND.white, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>THE LAB</h2>
        <p style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: BRAND.accent, marginBottom: 16 }}>Recovery Protocol</p>
        <p style={{ fontSize: 15, color: BRAND.textSecondary, maxWidth: 420, margin: "0 auto", lineHeight: 1.6 }}>Mobile recovery unit delivering hydration, fuel, and reset protocols wherever the night went too hard.</p>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <StatusDot status="active" />
          <span style={{ fontSize: 12, color: BRAND.accent, letterSpacing: "0.06em" }}>Shipping across Arizona</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 0, padding: "0 20px", borderBottom: `1px solid ${BRAND.borderGray}`, marginBottom: 28 }}>
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: "14px 20px", background: "none", border: "none", borderBottom: `2px solid ${activeTab === tab.id ? BRAND.accent : "transparent"}`, color: activeTab === tab.id ? BRAND.white : BRAND.textMuted, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}>{tab.label}</button>
        ))}
      </div>

      <main style={{ maxWidth: 680, margin: "0 auto", padding: "0 20px 80px" }}>
        {activeTab === "kits" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeUp 0.4s ease" }}>
            {PRODUCTS.map((p) => <ProductCard key={p.id} product={p} onAdd={cart.addItem} />)}
            <p style={{ fontSize: 11, color: BRAND.textMuted, textAlign: "center", marginTop: 8 }}>Wellness support only. Not medical treatment. Flat rate $8 shipping within Arizona.</p>
          </div>
        )}
        {activeTab === "schedule" && (
          <div style={{ animation: "fadeUp 0.4s ease" }}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: BRAND.white, marginBottom: 4 }}>Van Schedule</h3>
              <p style={{ fontSize: 13, color: BRAND.textSecondary }}>Find the Mobile Recovery Unit near you this week.</p>
            </div>
            <VanSchedule schedule={schedule} />
            <p style={{ fontSize: 11, color: BRAND.textMuted, textAlign: "center", marginTop: 16 }}>Schedule subject to change. Follow @thelabrecovery for real-time updates.</p>
          </div>
        )}
        {activeTab === "protocol" && (
          <div style={{ animation: "fadeUp 0.4s ease" }}>
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: BRAND.white, marginBottom: 4 }}>Recovery Protocol</h3>
              <p style={{ fontSize: 13, color: BRAND.textSecondary }}>Follow the steps. Reset the system.</p>
            </div>
            {[
              { step: 1, title: "Hydrate", desc: "Drink the hydration first. Consume slowly over 5-10 minutes.", wait: "Wait 10 minutes" },
              { step: 2, title: "Fuel", desc: "Eat the protein bar to stabilize energy and blood sugar. Small bites.", wait: "Wait 15 minutes" },
              { step: 3, title: "Stabilize", desc: "Use ginger or peppermint as needed for nausea. Breathe. Sit upright.", wait: "Wait 10 minutes" },
              { step: 4, title: "Reset", desc: "Drink additional water or coconut water. Rest. Light movement encouraged.", wait: "Reassess after 30 minutes" },
            ].map((s) => (
              <div key={s.step} style={{ padding: 20, background: BRAND.midGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, marginBottom: 12, borderLeft: `3px solid ${BRAND.accent}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <span style={{ width: 28, height: 28, borderRadius: "50%", background: BRAND.accent, color: BRAND.black, fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono', monospace" }}>{s.step}</span>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: BRAND.white }}>{s.title}</h4>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: BRAND.textSecondary, lineHeight: 1.6, paddingLeft: 40 }}>{s.desc}</p>
                <p style={{ margin: "10px 0 0", fontSize: 11, color: BRAND.accent, letterSpacing: "0.08em", textTransform: "uppercase", paddingLeft: 40 }}>Timer: {s.wait}</p>
              </div>
            ))}
            <div style={{ marginTop: 20, padding: 16, background: BRAND.darkGray, borderRadius: 8, border: `1px solid ${BRAND.borderGray}`, textAlign: "center" }}>
              <p style={{ fontSize: 13, color: BRAND.textSecondary, marginBottom: 4 }}>Result: Improved hydration - Reduced discomfort - System reset</p>
              <p style={{ fontSize: 10, color: BRAND.textMuted }}>Wellness support only. Not medical treatment.</p>
            </div>
          </div>
        )}
      </main>

      <footer style={{ padding: "24px 20px", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)", borderTop: `1px solid ${BRAND.borderGray}`, textAlign: "center" }}>
        <p style={{ fontSize: 11, color: BRAND.textMuted, marginBottom: 8 }}>2025 The Lab - Recovery Protocol - Tucson, AZ</p>
        <button onClick={() => setView("ops-login")} style={{ background: "none", border: "none", color: BRAND.textMuted, fontSize: 10, cursor: "pointer", opacity: 0.4, letterSpacing: "0.1em", textTransform: "uppercase" }}>Operator Access</button>
      </footer>

      <CartDrawer cart={cart} open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={handleCheckout} />
    </div>
  );
}